<script lang="ts">
    import { type RequiredActionFunctionToolCall } from 'openai/resources/beta/threads/runs/runs'

    import { getItems } from '$lib/items'
    import { apiGetJson } from '$lib/proxy/apiProxy'
    import { getClients } from '$lib/stores/clients'
    import { EstimateStatus } from '$lib/stores/estimate/types'
    import { getEstimates } from '$lib/stores/estimates_list/helpers'
    import { type EstimatesParams } from '$lib/stores/estimates_list/types'
    import { allStatuses as allStatusesExp, type ExpensesParams } from '$lib/stores/expense/types'
    import { getExpensesList } from '$lib/stores/expenses/manager'
    import { getInvoices } from '$lib/stores/invoices/invoices'
    import { allStatuses as allStatusesInv, type InvoicesParams } from '$lib/stores/invoices/types'

    import OpenaiChat from './OpenaiChat.svelte'

	export let disabled = false

	export const focus = () => {handleFocus()}
	function handleFocus() {
		if(myChat) {
			myChat.focus()
		}
	}
	let myChat: OpenaiChat


	const functionCallHandler = async (call: RequiredActionFunctionToolCall) => {

		if (call?.function?.name === 'get_current_date') {
			const now = new Date()
			// Only return YYYY-MM-DD
			const data = {
				date: now.toISOString().split('T')[0] 
			}
			return JSON.stringify(data)
		} else if (call?.function?.name === 'get_clients') {
			const args = JSON.parse(call.function.arguments)
			const data = await getClientsData(args.name)
			return JSON.stringify(data)
		// get_client_info			
		} else if (call?.function?.name === 'get_invoices') {
			const args = JSON.parse(call.function.arguments)
			const data = await getInvoicesData(args.date_start, args.date_end)
			return JSON.stringify(data)
		} else if (call?.function?.name === 'get_invoice_info') {
			const args = JSON.parse(call.function.arguments)
			const data = await getInvoiceInfo(args.invoice_id)
			return JSON.stringify(data)
		} else if (call?.function?.name === 'get_estimates') {
			const args = JSON.parse(call.function.arguments)
			const data = await getEstimatesData(args.date_start, args.date_end)
			return JSON.stringify(data)
		} else if (call?.function?.name === 'get_estimate_info') {
			const args = JSON.parse(call.function.arguments)
			const data = await getEstimateInfo(args.estimate_id)
			return JSON.stringify(data)
		} else if (call?.function?.name === 'get_items') {
			const args = JSON.parse(call.function.arguments)
			const data = await getItemsData(args.item_name, args.item_id)
			return JSON.stringify(data)
		} else if (call?.function?.name === 'get_item_info') {
			const args = JSON.parse(call.function.arguments)
			const data = await getItemInfo(args.item_id)
			return JSON.stringify(data)
		} else if (call?.function?.name === 'get_expenses') {
			const args = JSON.parse(call.function.arguments)
			const data = await getExpensesData(args.date_start, args.date_end)
			return JSON.stringify(data)
		}
		
		return ''
	}


	// Functions
	const getClientsData = async (name: string) => {
		
		let qryParams = {
			archived: [false],
			query: '',
			withName: true,
			clientId: ''
		}
		if (name) qryParams.query = name

		let theData = await getClients(qryParams)
		return theData
	}

	const getInvoicesData = async (date_start: string, date_end: string) => {
		
		let qryParams: InvoicesParams = {
			statuses: allStatusesInv,
			archived: [false],
			query: '',
			invoiceId: '',

			itemId: '',

			clientId: '',
			invoices_filter: '',

			date_start: null,
			date_end:  null
		}

		if (date_start) 
			qryParams.date_start = new Date(date_start)

		if (date_end) 
			qryParams.date_end = new Date(date_end)


		const theData = await getInvoices(qryParams)
		return theData
	}

	const getEstimatesData = async (date_start: string, date_end: string) => {

		const allStatuses = [
			EstimateStatus.Draft,
			EstimateStatus.Sent,
			EstimateStatus.Viewed,
			EstimateStatus.Invoiced,
			EstimateStatus.Approved
		]
		let qryParams: EstimatesParams = {
			statuses: allStatuses,
			archived: [false],
			query: '',
			estimateId: '',

			itemId: '',

			clientId: '',

			date_start: null,
			date_end:  null
		}

		if (date_start) 
			qryParams.date_start = new Date(date_start)

		if (date_end) 
			qryParams.date_end = new Date(date_end)


		const theData = await getEstimates(qryParams)
		return theData
	}

	const getItemsData = async (query: string, item_id: string) => {

		let qryParams = {
			archived: [false],
			query: '',
			withName: true,
			item_id: '',
			categories: [],
			subcategories: []
		}

		if (query && (typeof query !== 'undefined')) 
			qryParams.query = query

		if (item_id && (typeof item_id !== 'undefined'))
			qryParams.item_id = item_id


		const theData = await getItems(qryParams)
		return theData
	}		

	const getExpensesData = async (date_start: string, date_end: string) => {

		let qryParams: ExpensesParams = {
			statuses: allStatusesExp,
			archived: [false],
			query: '',
			date_start: null,
			date_end:  null
		}

		if (date_start) 
			qryParams.date_start = new Date(date_start)

		if (date_end) 
			qryParams.date_end = new Date(date_end)


		const theData = await getExpensesList(qryParams, false)
		return theData
	}


	const getInvoiceInfo = async (id: string) => {

		const theUrl = '/invoices/read/' + id
		const jsonData = await apiGetJson(theUrl)
		// const jsonData = await apiFetchJson(fetch, 'get', theUrl)

		if (!jsonData) return
		if (jsonData?.error) {
			return { errorMsg: jsonData.error }
		}

		return jsonData
	}

	const getEstimateInfo = async (id: string) => {
	
		const theUrl = '/estimate/read/' + id
		const jsonData = await apiGetJson(theUrl)
		// const jsonData = await apiFetchJson(fetch, 'get', theUrl)

		if (!jsonData) return
		if (jsonData?.error) {
			return { errorMsg: jsonData.error }
		}

		return jsonData
	}

	const getItemInfo = async (id: string) => {
	
		const jsonResponse = await apiGetJson('/items/get', { id })
		if (jsonResponse?.error) {
			return { errorMsg: jsonResponse.error }
		}

		//const data = parseServerItem(jsonResponse.item)	
		return jsonResponse.item
	}


</script>

<div class="w-full">
	<OpenaiChat 
		bind:this={myChat}
		{functionCallHandler} 
		{disabled} 
	/>
</div>

